import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import { getFusion } from '../../../apiHelper/fusionApi'
import { CFormLabel } from '@coreui/react-pro'
import { GRAY_300, GRAY_400, GRAY_800, GRAY_950, WHITE } from '../../../scss/colorConsts'
import { getTheme } from '../../../redux/user/userSlice'
import { getPCPObject } from '../../../components/common/shared/sharedPatientCode'

const selectStyleDark = {
  control: (styles, { isDisabled }) => ({
    ...styles,
    backgroundColor: 'var(--reactselect-input-bg-color)',
    borderColor: isDisabled ? 'transparent' : GRAY_400,
    boxShadow: 'none',
  }),
  input: (styles) => ({
    ...styles,
    color: 'var(--reactselect-input-color)',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: 'grey',
  }),
  option: (styles, { data, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isSelected
        ? 'var(--text-glow-color)'
        : isFocused
          ? 'var(--text-glow-color)'
          : GRAY_800,
      color: isSelected ? GRAY_950 : isFocused ? GRAY_950 : data?.color,
      cursor: 'pointer',
      borderBottom: `1px solid ${GRAY_300}`,
      'last-child': {
        border: 'none',
      },
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--text-glow-color)',
      },
    }
  },
  singleValue: (styles, { data }) => ({ ...styles, color: GRAY_300 }),
  placeholder: (styles, { data }) => ({ ...styles, color: GRAY_300 }),
}

const selectStyleLight = {
  control: (styles) => ({
    ...styles,
    backgroundColor: 'var(--reactselect-input-bg-color)',
    boxShadow: 'none',
  }),
  input: (styles) => ({ ...styles, color: 'var(--reactselect-input-color)' }),
  menu: (styles) => ({ ...styles, backgroundColor: 'var(--reactselect-input-bg-color)' }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isSelected
        ? 'var(--text-glow-color)'
        : isFocused
          ? 'var(--text-glow-color)'
          : WHITE,
      color: GRAY_950,
      cursor: 'pointer',
      borderBottom: `1px solid ${GRAY_300}`,
      'last-child': {
        border: 'none',
      },
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--text-glow-color)',
      },
    }
  },
  singleValue: (styles, { data }) => ({ ...styles, color: GRAY_300 }),
  placeholder: (styles, { data }) => ({ ...styles, color: GRAY_300 }),
}

const specialItems = ['No PCP Listed', 'Non-Invoice PCP']

async function fetchPcpOptions(searchString) {
  try {
    let res = await getFusion('v1/patient/pcp/search?q=' + searchString)
    if (res?.status !== 'failure') {
      return res.results
        .map((pcp) => {
          return {
            label: pcp.name + '-' + pcp.npi,
            name: pcp.name,
            value: pcp.npi,
            pcp_id: pcp._id,
          }
        })
        .sort((a, b) => {
          const aIsSpecial = specialItems.includes(a.name)
          const bIsSpecial = specialItems.includes(b.name)

          // If both are special items, sort them in reverse order (Z-A) between themselves
          if (aIsSpecial && bIsSpecial) {
            return b.name.localeCompare(a.name)
          }

          // If only a is special, it should come first
          if (aIsSpecial) {
            return -1
          }

          // If only b is special, it should come first
          if (bIsSpecial) {
            return 1
          }

          // For all other items, sort alphabetically (A-Z)
          return a.name.localeCompare(b.name)
        })
    }
  } catch (e) {
    console.error(e)
  }

  return []
}

export default function PrimaryCarePhysicianDropdown({ primary_care_physician, setValues }) {
  const theme = useSelector(getTheme)
  const [pccgTitle, setPccgTitle] = useState(null)

  useEffect(() => {
    getPCPObject(primary_care_physician?.primary_care_physician_id).then((res) => {
      setPccgTitle(res?.group && res.county ? `${res.group} - ${res.county}` : null)
    })
  }, [primary_care_physician?.primary_care_physician_id])

  return (
    <div className="item-margin">
      <CFormLabel className="label">Primary Care Physician</CFormLabel>
      <div className="select-box-holder">
        <AsyncSelect
          isSearchable
          loadOptions={fetchPcpOptions}
          noOptionsMessage={() => 'No results found'}
          onChange={(newPCP) => {
            setValues((oldValues) => ({
              ...oldValues,
              primary_care_physician: {
                name: newPCP.name,
                npi: newPCP.value,
                primary_care_physician_id: newPCP.pcp_id,
              },
            }))
          }}
          styles={theme === 'light' ? selectStyleLight : selectStyleDark}
          value={primary_care_physician?.npi}
          placeholder={
            primary_care_physician
              ? primary_care_physician.name + '-' + primary_care_physician.npi
              : ' '
          }
          components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
        />
      </div>

      <CFormLabel className="label pt-2">Primary Care Physician Group</CFormLabel>
      <div className="select-box-holder">
        <Select
          isDisabled
          styles={theme === 'light' ? selectStyleLight : selectStyleDark}
          placeholder={pccgTitle || (primary_care_physician ? 'N/A' : '')}
          components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
        />
      </div>
    </div>
  )
}
